import {compact, isNull, keyBy} from 'lodash';

import {
  readApiModel,
  readApiModelInIdleCallbackAsync,
} from '../../../../libs/shared/core/src/helpers/api/apiHelpers';
import {addQueryToPath} from '../../../../libs/shared/core/src/helpers/browser/urlHelpers';
import {isMobile} from '../../../../libs/shared/core/src/helpers/environment/mobileEnvHelpers';
import {CancelToken} from '../../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {AnnotationModel} from '../../../../libs/shared/core/src/models/api/annotationModel';
import {fetchApiAsync, fetchAsync} from '../../../../libs/shared/core/src/services/http/http';
import {logger} from '../../../../libs/shared/core/src/services/logger/logger';
import {
  createDeletableResourceTable,
  createLazyResourceTable,
  createResourceTable,
  emptyResourceTable,
} from '../../../../libs/shared/core/src/store/commonAppTypes';
import {
  ApiBoot,
  ApiInboxesAndChannelsBootPart,
  ApiTeammatesBootPart,
  AppLanguagesEnum,
  BootModel,
} from '../../../../src/models/api/bootModel';
import {DataGroupModel} from '../../../../src/models/api/dataGroupModel';
import {BootData} from '../../../../src/store/data/dataActions';
import {bootPath} from '../bootConstants';

/*
 * Constants.
 */

const bootNetworkTimeout = 60 * 1000;
const shouldFetchCannedAnswers = !isMobile();

/*
 * API.
 */

export interface FetchBootResult {
  rawBoot: unknown;
  data: BootData;
  extraDataGroups: ReadonlyArray<DataGroupModel>;
}

/*
 * Helpers.
 */

export async function fetchRawBootInPartsAsync(
  companyUrl: string,
  cancelToken?: CancelToken,
): Promise<unknown> {
  const [
    rawPartialBoot,
    {fetchedAt: teammatesFetchedAt, rawTeammates},
    {fetchedAt: inboxesAndChannelsFetchedAt, rawInboxes, rawChannels},
    rawCannedAnswers,
  ] = await Promise.all([
    fetchRawBootAsync(companyUrl, {
      excludeResourceTypes: ['team', 'inboxes', 'channels', 'canned_answers'],
      cancelToken,
    }) as Promise<any>,
    fetchRawBootTeammatesAsync(companyUrl, 0, null, cancelToken),
    fetchRawBootInboxesAndChannelsAsync(companyUrl, true, 0, null, cancelToken),
    fetchRawBootCannedAnswersAsync(companyUrl, cancelToken),
  ]);

  return {
    ...rawPartialBoot,
    ts: Math.min(rawPartialBoot.ts, teammatesFetchedAt, inboxesAndChannelsFetchedAt),
    team: rawTeammates,
    inboxes: rawInboxes,
    channels: rawChannels,
    canned_answers: rawCannedAnswers,
  };
}

interface RawBootTeammatesResult {
  fetchedAt: number;
  rawTeammates: ReadonlyArray<unknown>;
}

const emptyRawBootTeammatesResult: RawBootTeammatesResult = {
  fetchedAt: Infinity,
  rawTeammates: [],
};

export async function fetchRawBootTeammatesAsync(
  companyUrl: string,
  fetchedCount: number,
  checkpoint: number | string | null,
  cancelToken?: CancelToken,
): Promise<RawBootTeammatesResult> {
  // For the first page, omit the checkpoint parameter.
  const query = isNull(checkpoint) ? undefined : {checkpoint};
  const path = addQueryToPath(`${companyUrl}/boot/team`, query);

  // Fetch the current page.
  const part = await fetchApiAsync(ApiTeammatesBootPart, path, {
    cancelToken,
    timeout: bootNetworkTimeout,
  });
  const newFetchedCount = fetchedCount + part.teammates.length;

  // Guard against pagination loops.
  if (!isNull(part.checkpoint) && (part.checkpoint === checkpoint || newFetchedCount > part.total)) {
    throw new Error('Unexpected teammates boot part checkpoint');
  }

  // Fetch the next page, if any.
  const nextResult: RawBootTeammatesResult = isNull(part.checkpoint)
    ? emptyRawBootTeammatesResult
    : await fetchRawBootTeammatesAsync(companyUrl, newFetchedCount, part.checkpoint, cancelToken);

  return {
    fetchedAt: Math.min(part.fetchedAt, nextResult.fetchedAt),
    rawTeammates: [...part.teammates, ...nextResult.rawTeammates],
  };
}

interface RawBootInboxesAndChannelsResult {
  fetchedAt: number;
  rawInboxes: ReadonlyArray<unknown>;
  rawChannels: ReadonlyArray<unknown>;
}

const emptyRawBootInboxesAndChannelsResult: RawBootInboxesAndChannelsResult = {
  fetchedAt: Infinity,
  rawInboxes: [],
  rawChannels: [],
};

// TODO: Reduce duplication with fetchRawBootTeammatesAsync.
export async function fetchRawBootInboxesAndChannelsAsync(
  companyUrl: string,
  shouldFetchOnlyAccessibleInboxes: boolean,
  fetchedCount: number,
  checkpoint: number | string | null,
  cancelToken?: CancelToken,
): Promise<RawBootInboxesAndChannelsResult> {
  const query = {
    checkpoint: checkpoint ?? undefined,
    accessible: shouldFetchOnlyAccessibleInboxes,
  };
  const path = addQueryToPath(`${companyUrl}/boot/inboxes`, query);

  // Fetch the current page.
  const part = await fetchApiAsync(ApiInboxesAndChannelsBootPart, path, {
    cancelToken,
    timeout: bootNetworkTimeout,
  });
  const newFetchedCount = fetchedCount + part.inboxes.length;

  // Guard against pagination loops.
  if (!isNull(part.checkpoint) && (part.checkpoint === checkpoint || newFetchedCount > part.total)) {
    throw new Error('Unexpected inboxes boot part checkpoint');
  }

  // Fetch the next page, if any.
  const nextResult: RawBootInboxesAndChannelsResult = isNull(part.checkpoint)
    ? emptyRawBootInboxesAndChannelsResult
    : await fetchRawBootInboxesAndChannelsAsync(
        companyUrl,
        shouldFetchOnlyAccessibleInboxes,
        newFetchedCount,
        part.checkpoint,
        cancelToken,
      );

  return {
    fetchedAt: Math.min(part.fetchedAt, nextResult.fetchedAt),
    rawInboxes: [...part.inboxes, ...nextResult.rawInboxes],
    rawChannels: [...part.channels, ...nextResult.rawChannels],
  };
}

interface FetchRawBootOptions {
  deltasSince?: number;
  refreshToken?: string;
  excludeResourceTypes?: ReadonlyArray<string>;
  hasServiceWorkerBootCachePatching?: boolean;
  cancelToken?: CancelToken;
}

export async function fetchRawBootAsync(
  companyUrl: string,
  options: FetchRawBootOptions = {},
): Promise<unknown> {
  const {deltasSince, excludeResourceTypes, hasServiceWorkerBootCachePatching, cancelToken} = options;
  const url = addQueryToPath(`${companyUrl}${bootPath}`, {
    ts: Date.now(),
    deltas_since: deltasSince,
    refresh_token: options.refreshToken,
    exclude: excludeResourceTypes ? excludeResourceTypes.join(',') : undefined,
    sw_cache_patch: hasServiceWorkerBootCachePatching ? '1' : undefined,
  });
  logger.info('Fetching raw boot', {url});
  return await fetchAsync(url, {cancelToken, timeout: bootNetworkTimeout});
}

export async function fetchRawBootCannedAnswersAsync(
  companyUrl: string,
  cancelToken?: CancelToken,
): Promise<unknown> {
  if (!shouldFetchCannedAnswers) {
    return [];
  }

  const body = await fetchAsync(`${companyUrl}/canned_answers`, {cancelToken, timeout: bootNetworkTimeout});
  return body.canned_answers;
}

export async function readDataFromBoot(rawBoot: unknown, isRefresh?: boolean): Promise<FetchBootResult> {
  const boot = isRefresh
    ? await readApiModelInIdleCallbackAsync(ApiBoot, rawBoot)
    : readApiModel(ApiBoot, rawBoot);
  const data = mapBootToData(boot);

  const extraDataGroups = compact([boot.conversationDataGroup]);
  return {rawBoot, data, extraDataGroups};
}

function mapBootToData(boot: BootModel): BootData {
  return {
    config: {
      host: boot.host,
      buildVersion: boot.buildVersion,
      push: boot.pushConfig,
      chatPush: boot.chatPushConfig,
      helpChat: boot.helpChatConfig,
      launchdarkly: boot.launchdarkly,
      isImpersonating: boot.isImpersonating,
      allowedDomainForEmbed: boot.allowedDomainForEmbed,
      language: AppLanguagesEnum.ENGLISH,
      frontMailDomainName: boot.frontMailDomainName,
    },
    preferences: boot.preferences,
    userMetadata: boot.userMetadata,
    counters: boot.counters,
    company: boot.company,
    userId: boot.user.id,
    bootFetchedAt: boot.fetchedAt,
    bootRefreshToken: boot.refreshToken,
    deltas: boot.deltas,
    teams: createResourceTable(
      keyBy(boot.teams.all, (t) => t.id),
      boot.teams.deletedIds,
    ),
    teammates: {
      ...keyBy(boot.teammates, (t) => t.id),
      [boot.user.id]: boot.user,
    },
    circles: createResourceTable(
      keyBy(boot.circles.all, (c) => c.id),
      boot.circles.deletedIds,
    ),
    delegations: createResourceTable(
      keyBy(boot.receivedDelegations.all, (t) => t.id),
      boot.receivedDelegations.deletedIds,
    ),
    inboxes: createDeletableResourceTable(
      keyBy(boot.inboxes.all, (t) => t.id),
      boot.inboxes.deletedIds,
      keyBy(boot.inboxes.deletedById, (t) => t.id),
    ),
    channels: createResourceTable(
      keyBy(boot.channels.all, (t) => t.id),
      boot.channels.deletedIds,
    ),
    answers: createLazyResourceTable(
      keyBy(boot.answers.all, (a) => a.id),
      boot.answers.deletedIds,
    ),
    answerFolders: createLazyResourceTable(
      keyBy(boot.answerFolders.all, (a) => a.id),
      boot.answerFolders.deletedIds,
    ),
    signatures: createLazyResourceTable(
      keyBy(boot.signatures.all, (s) => s.id),
      boot.signatures.deletedIds,
    ),
    integrations: createResourceTable(
      keyBy(boot.integrations.all, (t) => t.id),
      boot.integrations.deletedIds,
    ),
    legacyApplications: createResourceTable(
      keyBy(boot.legacyApplications.all, (a) => a.id),
      boot.legacyApplications.deletedIds,
    ),
    customViews: createResourceTable(
      keyBy(boot.customViews.all, (c) => c.id),
      boot.customViews.deletedIds,
    ),
    customFields: createResourceTable(
      keyBy(boot.customFields.all, (c) => c.id),
      boot.customFields.deletedIds,
    ),
    calendarVersions: createResourceTable(
      keyBy(boot.calendarVersions.all, (v) => v.id),
      boot.calendarVersions.deletedIds,
    ),
    calendars: createResourceTable(
      keyBy(boot.calendars.all, (c) => c.id),
      boot.calendars.deletedIds,
    ),
    roles: createResourceTable(
      keyBy(boot.roles.all, (r) => r.alias),
      boot.roles.deletedIds,
    ),
    jobs: createResourceTable(
      keyBy(boot.jobs.all, (r) => r.id),
      boot.jobs.deletedIds,
    ),
    contacts: keyBy(boot.contacts, (c) => c.uid),
    tags: createDeletableResourceTable(
      keyBy(boot.tags.all, (t) => t.id),
      boot.tags.deletedIds,
      keyBy(boot.tags.deletedById, (t) => t.id),
    ),
    lenses: createResourceTable(
      keyBy(boot.lenses.all, (lens) => lens.id),
      boot.lenses.deletedIds,
    ),
    accounts: createResourceTable({}),
    cards: createResourceTable({}),
    cardGroups: createResourceTable({}),
    cardNotes: createResourceTable({}),
    conversations: createResourceTable({}),
    pinnedActivityLists: emptyResourceTable,
    inquiryLists: emptyResourceTable,
    similarInquiryLists: emptyResourceTable,
    relatedKnowledgeLists: emptyResourceTable,
    activities: createResourceTable({}),
    messages: {},
    comments: emptyResourceTable,
    calendarEvents: {},
    calendarEventVersions: {},
    workspace: boot.workspace,
    topics: createResourceTable({}),
    localApplications: createResourceTable({}),
    apiTokens: createResourceTable({}),
    smtpDomains: boot.smtpDomains,
    annotations: createResourceTable<string, AnnotationModel>({}),
  };
}
